import { parseLocalDate, toLocalDateKey } from './dateUtils'
import { coerceTourShowDate, getTourShowDateRaw } from './tourShowListHelpers'

export interface BookingLite {
  id: string
  perf_date?: string
  date?: string
  venue?: string
  city?: string
  state?: string
  artist?: string
  raw?: any
}

export function unwrapTmShowPayload(payload: unknown): Record<string, unknown> | null {
  if (!payload || typeof payload !== 'object') return null
  const p = payload as { data?: unknown }
  const inner = p.data !== undefined ? p.data : payload
  if (inner && typeof inner === 'object' && !Array.isArray(inner)) return inner as Record<string, unknown>
  return null
}

function normVenueFold(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, '').trim()
}

function isTbdPerf(raw: unknown): boolean {
  const s = String(raw ?? '').trim().toLowerCase()
  return s === 'tbd' || s === 't.b.d.' || s === 'to be determined' || s === ''
}

export function findTourShowIdForBooking(
  bookingId: string,
  show: Pick<BookingLite, 'perf_date' | 'date' | 'venue' | 'city' | 'state' | 'raw'> | null | undefined,
  tmShows: Record<string, unknown>[],
): string | null {
  if (!bookingId || !tmShows.length) return null
  for (const s of tmShows) {
    const bid = s.bookingId ?? (s as { booking_id?: unknown }).booking_id
    if (bid != null && String(bid) === String(bookingId)) return String(s._id)
  }
  const raw = show?.raw as Record<string, unknown> | undefined
  const hint = raw?.tmShowId ?? raw?.tourShowId
  if (hint) {
    const id = String(hint)
    if (tmShows.some((x) => String(x._id) === id)) return id
  }
  if (!show) return null
  const perfRaw = show.perf_date || show.date
  let showKey = ''
  if (perfRaw && !isTbdPerf(perfRaw)) {
    const pd = parseLocalDate(perfRaw)
    const dt = pd && !Number.isNaN(pd.getTime()) ? pd : new Date(String(perfRaw))
    if (!Number.isNaN(dt.getTime())) showKey = toLocalDateKey(dt)
  }
  const showVenue = normVenueFold(show.venue || '')
  const showCity = (show.city || '').toLowerCase().trim()
  if (!showKey || (!showVenue && !showCity)) return null
  for (const s of tmShows) {
    const d = coerceTourShowDate(getTourShowDateRaw(s))
    const tmKey = d && !Number.isNaN(d.getTime()) ? toLocalDateKey(d) : ''
    const v = s.venue as { name?: string; city?: string } | undefined
    const tmVenue = normVenueFold(v?.name || '')
    const tmCity = (v?.city || '').toLowerCase().trim()
    if (showKey && tmKey && showKey === tmKey) {
      if (showVenue && tmVenue && (tmVenue.includes(showVenue) || showVenue.includes(tmVenue))) {
        return String(s._id)
      }
      if (showCity && tmCity && showCity === tmCity) return String(s._id)
    }
  }
  return null
}

export function buildTmBulkPayloadFromShow(show: BookingLite): Record<string, unknown> {
  const r = show.raw && typeof show.raw === 'object' ? (show.raw as Record<string, unknown>) : {}
  const cityState = String(r.city_state ?? '').trim()
  let city = show.city || ''
  let state = show.state || ''
  if (cityState) {
    const parts = cityState.split(',').map((p) => p.trim())
    if (!city && parts[0]) city = parts[0]
    if (!state && parts[1]) state = parts[1]
  }
  const perf = show.perf_date || show.date || ''
  const schedule = String(r.schedule ?? r.show_time ?? '')
  return {
    showDate: perf,
    venue: {
      name: show.venue || 'Unknown Venue',
      city: city || 'TBD',
      state: state || '',
      country: String(r.country ?? 'USA'),
      address: '',
    },
    showInfo: {
      artist: show.artist || 'Unknown Artist',
      showtimes: schedule,
      doors: '',
      soundcheck: '',
    },
    status: 'planning',
    bookingId: show.id,
    notes: String(r.notes ?? r.advanced_info ?? ''),
    tags: [],
  }
}

export function tmListFromApiPayload(payload: unknown, key: string): unknown[] {
  if (Array.isArray(payload)) return payload
  if (!payload || typeof payload !== 'object') return []
  const d = (payload as { data?: unknown }).data
  if (Array.isArray(d)) return d
  if (d && typeof d === 'object' && !Array.isArray(d)) {
    const arr = (d as Record<string, unknown>)[key]
    if (Array.isArray(arr)) return arr
  }
  return []
}

/** `user/tm/*` list responses use `apiResponse` with nested keys — not a bare array. */
export function tmTravelListFromApi(
  payload: unknown,
  kind: 'flights' | 'hotels' | 'ground' | 'crew',
): unknown[] {
  const key = kind === 'ground' ? 'transports' : kind
  return tmListFromApiPayload(payload, key)
}
